import { PropsWithChildren, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import type { AlertColor } from '@mui/material/Alert';
import { mergeSx } from 'components/ui/utilities/mergeSx';
import { AlertProps } from './Alert.props';
import { getAlertInlineSx, StyledAlert } from './Alert.styles';

const Alert = ({
  children,
  onClose,
  severity = 'info',
  sx,
  visualVariant,
  ...props
}: PropsWithChildren<AlertProps>) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const resolvedSeverity = severity as AlertColor;
  const isInlineVariant = visualVariant === 'inline' || visualVariant === 'inlineError';
  const presetSx = useMemo(() => {
    if (isInlineVariant) {
      return getAlertInlineSx(resolvedSeverity);
    }

    return undefined;
  }, [isInlineVariant, resolvedSeverity]);
  const mergedSx = mergeSx(presetSx, sx);
  const isInlineError = visualVariant === 'inlineError' && resolvedSeverity === 'error';
  const isClosable = useMemo(() => isInlineError || Boolean(onClose), [isInlineError, onClose]);

  useEffect(() => {
    setIsDismissed(false);
  }, [children, severity, visualVariant]);

  if (isDismissed) {
    return null;
  }

  const handleClose = (event: SyntheticEvent) => {
    onClose?.(event);
    if (!onClose || isInlineError) {
      setIsDismissed(true);
    }
  };

  return (
    <StyledAlert onClose={isClosable ? handleClose : undefined} severity={severity} sx={mergedSx} {...props}>
      {children}
    </StyledAlert>
  );
};

export default Alert;
